import axiosInstance from './axiosInstance';

export interface Pharmacy {
  id?: string;
  _id?: string; // MongoDB ObjectId
  name: string;
  pharmacyName: string;
  firstName: string;
  lastName: string;
  address?: {
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
  };
  location?: any,
  phone: string;
  email: string;
  imageURL?: string;
  isActive: boolean;
  isVerified: boolean;
  accountStatus: 'pending' | 'verified' | 'rejected' | 'suspended';
  accountType: string;
  role: string;
  totalOrders: number;
  activeOrders: number;
  createdAt: string;
  updatedAt: string;
  medicines: any[];
  ordersPlaced: any[];
  ordersReceived: any[];
  notifications: any[];
  notificationSettings: {
    orderUpdates: boolean;
    pickupRequests: boolean;
    deliveryAlerts: boolean;
    systemMaintenance: boolean;
  };
}

export interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseNumber: string;
  vehicleType: string;
  vehiclePlate: string;
  status: 'online' | 'offline' | 'on-delivery' | 'break';
  currentLocation?: {
    lat: number;
    lng: number;
    address: string;
  };
  rating: number;
  totalDeliveries: number;
  completedToday: number;
  joinedDate: string;
  isActive: boolean;
  currentRoute?: string;
  earnings: {
    today: number;
    week: number;
    month: number;
  };
}

class PharmacyService {
  private baseEndpoint = '/admin/pharmacies';

  async getAllPharmacies(page: number = 1, limit: number = 10, filters: any = {}): Promise<{pharmacies: Pharmacy[], pagination: any}> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters
      });
      
      console.log('PharmacyService: Fetching pharmacies from:', `${this.baseEndpoint}?${params}`);
      const response = await axiosInstance.get(`${this.baseEndpoint}?${params}`);
      console.log('PharmacyService: Full API response:', response.data);
      console.log('PharmacyService: Response status:', response.status);
      
      // Handle admin pharmacies endpoint structure
      let pharmacies = [];
      let pagination = {};
      
      if (response.data?.data?.pharmacies) {
        pharmacies = response.data.data.pharmacies;
        pagination = response.data.data.pagination || {};
        console.log('PharmacyService: Using response.data.data.pharmacies structure');
      } else if (response.data?.pharmacies) {
        pharmacies = response.data.pharmacies;
        pagination = response.data.pagination || {};
        console.log('PharmacyService: Using response.data.pharmacies structure');
      } else if (Array.isArray(response.data)) {
        pharmacies = response.data;
        console.log('PharmacyService: Using direct array structure');
      } else {
        console.warn('PharmacyService: Unexpected API response structure:', response.data);
        return { pharmacies: [], pagination: {} };
      }

      console.log('PharmacyService: Processed pharmacies:', pharmacies.length, 'pharmacies');
      console.log('PharmacyService: Pagination:', pagination);
      if (pharmacies.length > 0) {
        console.log('PharmacyService: First pharmacy:', pharmacies[0]);
      }

      return { pharmacies, pagination };
    } catch (error) {
      console.error('PharmacyService: Error fetching pharmacies:', error);
      if (error.response) {
        console.error('PharmacyService: Error response:', error.response.data);
        console.error('PharmacyService: Error status:', error.response.status);
      }
      return { pharmacies: [], pagination: {} };
    }
  }

  async getPharmacyStats(): Promise<any> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/stats`);
      console.log('PharmacyService: Stats response:', response.data);
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching pharmacy stats:', error);
      return {};
    }
  }

  async getPharmacyById(id: string): Promise<Pharmacy | null> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/${id}`);
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching pharmacy:', error);
      return null;
    }
  }

  async updatePharmacyStatus(id: string, accountStatus: string, isVerified: boolean, notes?: string): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${id}/status`, {
        accountStatus,
        isVerified,
        notes: notes || `Status updated to ${accountStatus}`
      });
      return response.data?.success || false;
    } catch (error) {
      console.error('Error updating pharmacy status:', error);
      return false;
    }
  }

  async approvePharmacy(id: string, notes?: string): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${id}/status`, {
        accountStatus: 'verified',
        isVerified: true,
        notes: notes || 'Pharmacy approved by admin'
      });
      return response.data?.success || false;
    } catch (error) {
      console.error('Error approving pharmacy:', error);
      return false;
    }
  }

  async rejectPharmacy(id: string, notes?: string): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${id}/status`, {
        accountStatus: 'rejected',
        isVerified: false,
        notes: notes || 'Pharmacy rejected by admin'
      });
      return response.data?.success || false;
    } catch (error) {
      console.error('Error rejecting pharmacy:', error);
      return false;
    }
  }

  async deletePharmacy(id: string): Promise<boolean> {
    try {
      const response = await axiosInstance.delete(`${this.baseEndpoint}/${id}`);
      return response.data?.success || false;
    } catch (error) {
      console.error('Error deleting pharmacy:', error);
      return false;
    }
  }
}

class DriverService {
  private baseEndpoint = '/admin/riders';

  // Test admin authentication
  async testAdminAuth(): Promise<boolean> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/test`);
      return response.status === 200;
    } catch (error) {
      console.error('Error testing admin auth:', error);
      return false;
    }
  }

  async getAllDrivers(): Promise<Driver[]> {
    try {
      console.log('DriverService: Fetching riders from:', this.baseEndpoint);
      const response = await axiosInstance.get(this.baseEndpoint);
      console.log('DriverService: Full API response:', response.data);
      
      // Handle the new admin riders endpoint structure
      let riders = [];
      if (response.data?.data?.riders) {
        riders = response.data.data.riders;
      } else if (response.data?.riders) {
        riders = response.data.riders;
      } else if (Array.isArray(response.data)) {
        riders = response.data;
      } else {
        console.warn('Unexpected rider API response structure:', response.data);
        return [];
      }

      // Log each rider's ID structure for debugging
      console.log('DriverService: Processed riders:', riders.map(r => ({
        id: r.id,
        _id: r._id,
        name: r.name,
        isVerified: r.isVerified
      })));

      return riders;
    } catch (error) {
      console.error('Error fetching riders:', error);
      return [];
    }
  }

  async getRiderStats(): Promise<any> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/stats`);
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching rider stats:', error);
      return {};
    }
  }

  async getDriverById(id: string): Promise<Driver | null> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/${id}`);
      return response.data?.data || null;
    } catch (error) {
      console.error('Error fetching driver:', error);
      return null;
    }
  }

  async updateDriverStatus(id: string, status: Driver['status']): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${id}/status`, {
        accountStatus: status === 'online' ? 'verified' : 'suspended',
        isVerified: status === 'online',
        notes: `Status updated to ${status}`
      });
      return response.data?.success || false;
    } catch (error) {
      console.error('Error updating rider status:', error);
      return false;
    }
  }

  async approveRider(id: string): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${id}/status`, {
        accountStatus: 'verified',
        isVerified: true,
        notes: 'Rider approved by admin'
      });
      return response.data?.success || false;
    } catch (error) {
      console.error('Error approving rider:', error);
      return false;
    }
  }

  async rejectRider(id: string, notes?: string): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${id}/status`, {
        accountStatus: 'rejected',
        isVerified: false,
        notes: notes || 'Rider rejected by admin'
      });
      return response.data?.success || false;
    } catch (error) {
      console.error('Error rejecting rider:', error);
      return false;
    }
  }

  async deleteRider(id: string): Promise<boolean> {
    try {
      const response = await axiosInstance.delete(`${this.baseEndpoint}/${id}`);
      return response.data?.success || false;
    } catch (error) {
      console.error('Error deleting rider:', error);
      return false;
    }
  }

  async getDriversByStatus(status: Driver['status']): Promise<Driver[]> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}?status=${status}`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching drivers by status:', error);
      return [];
    }
  }

  async assignRoute(riderId: string, routeData: { routeName: string; pickupLocation: string; deliveryLocation: string }): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${riderId}/assign-route`, routeData);
      return response.data?.success || false;
    } catch (error) {
      console.error('Error assigning route to rider:', error);
      return false;
    }
  }

  async unassignRoute(riderId: string): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${riderId}/unassign-route`);
      return response.data?.success || false;
    } catch (error) {
      console.error('Error unassigning route from rider:', error);
      return false;
    }
  }
}

// Export singleton instances
export const pharmacyService = new PharmacyService();
export const driverService = new DriverService();

// Export convenience functions
export const getAllPharmacies = (page?: number, limit?: number, filters?: any) => 
  pharmacyService.getAllPharmacies(page, limit, filters);
export const getPharmacyById = (id: string) => pharmacyService.getPharmacyById(id);
export const getPharmacyStats = () => pharmacyService.getPharmacyStats();
export const updatePharmacyStatus = (id: string, accountStatus: string, isVerified: boolean, notes?: string) => 
  pharmacyService.updatePharmacyStatus(id, accountStatus, isVerified, notes);
export const approvePharmacy = (id: string, notes?: string) => pharmacyService.approvePharmacy(id, notes);
export const rejectPharmacy = (id: string, notes?: string) => pharmacyService.rejectPharmacy(id, notes);
export const deletePharmacy = (id: string) => pharmacyService.deletePharmacy(id);

export const getAllDrivers = () => driverService.getAllDrivers();
export const getDriverById = (id: string) => driverService.getDriverById(id);
export const updateDriverStatus = (id: string, status: Driver['status']) => 
  driverService.updateDriverStatus(id, status);
export const getDriversByStatus = (status: Driver['status']) => 
  driverService.getDriversByStatus(status);
export const getRiderStats = () => driverService.getRiderStats();
export const approveRider = (id: string) => driverService.approveRider(id);
export const rejectRider = (id: string, notes?: string) => driverService.rejectRider(id, notes);
export const deleteRider = (id: string) => driverService.deleteRider(id);
export const testAdminAuth = () => driverService.testAdminAuth();
export const assignRoute = (riderId: string, routeData: { routeName: string; pickupLocation: string; deliveryLocation: string }) => 
  driverService.assignRoute(riderId, routeData);
export const unassignRoute = (riderId: string) => driverService.unassignRoute(riderId);
